/* eslint-disable @typescript-eslint/no-loop-func */
/**
 * Parallax Scroll Block Component
 * @module patterns/components/parallax-scroll-block
 */

import { App } from '~/app';

export default (app: App) => {
    app.logger.debug('[Stockland FE] Initialise Stockland Parallax Scroll Block Component');

    const listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]> = [];

    app.afterMount
        .then(() => {
            mountParallaxScrollBlock(listeners);
        })
        .catch((error) => {
            throw new Error('[Stockland FE] Unable to register Stockland Parallax Scroll Block component', error);
        });

    return function destroyParallaxScrollBlock() {
        unMountParallaxScrollBlock(listeners);

        app.logger.debug('[DXA] Parallax Scroll Block component destroyed');
    };
};

// Extracted method for logic reuse within other component
export function mountParallaxScrollBlock(
    listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]>,
    root: HTMLElement | null = null
) {
    let rootNode: Document | HTMLElement = document;
    if (root) rootNode = root;

    const parallaxContainers = rootNode.querySelectorAll<HTMLElement>('.parallax-container');

    for (const parallaxContainer of parallaxContainers) {
        const parallaxBlock = parallaxContainer.querySelector<HTMLElement>('.parallax-background');

        if (parallaxBlock) {
            const logo = parallaxBlock.querySelector<HTMLElement>('.logo-container');
            const header = parallaxBlock.querySelector<HTMLElement>('.header-container');
            const modals = parallaxBlock.querySelectorAll<HTMLElement>('.modal');
            const navigationBar = rootNode?.querySelector<HTMLElement>('header.navigation');
            const navigationBarHeight = navigationBar ? navigationBar.clientHeight : 0;
            const cards = parallaxBlock.querySelectorAll<HTMLElement>('.card');
            let scrollTop = parallaxContainer.scrollTop;

            const initialiseParallaxBackgroundLock = (entries: any) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        parallaxContainer.focus();

                        setTimeout(() => {
                            if (logo) {
                                logo.classList.add('parallax-show');
                            }
                        }, 1000);

                        setTimeout(() => {
                            if (header) {
                                header.classList.add('parallax-show');
                            }
                        }, 1500);

                        return;
                    }
                }
            };

            const initialiseParallaxScroll = (parallaxBlock: HTMLElement) => {
                const backgroundLayers = parallaxBlock?.querySelectorAll<HTMLElement>('.parallax-background-layer');
                const contentLayer = parallaxBlock?.querySelector<HTMLElement>('.parallax-content-layer');
                if (contentLayer) {

                    const handleScroll = () => {
                        const topOffSet = parallaxContainer.getBoundingClientRect().top;
                        scrollTop = document.documentElement.scrollTop - (window.scrollY + parallaxContainer.getBoundingClientRect().top);


                        if (topOffSet < navigationBarHeight && scrollTop + window.innerHeight < parallaxContainer.clientHeight) {

                            for (const [index, backgroundLayer] of backgroundLayers.entries()) {
                                const scrollIndicator = window.scrollY/(parallaxContainer.clientHeight-window.innerHeight);
                                if (scrollIndicator>=1){
                                    backgroundLayer.style.transform = "";
                                    backgroundLayer.style.top = "unset";
                                    backgroundLayer.style.bottom = "0";
                                    backgroundLayer.style.position = "absolute";
                                }
                                else{
                                    const ind= Math.pow(Math.min(Math.max(scrollIndicator,0),1), 1/(index/10 +1))
                                    const translationValue = -1* ind * (backgroundLayer.clientHeight-window.innerHeight);
                                    backgroundLayer.style.transform = `translate3d(0px, ${translationValue}px, 0px)`;
                                    backgroundLayer.style.top = "";
                                    backgroundLayer.style.bottom = "";
                                    backgroundLayer.style.position = ""
                                }
                            }

                            //setup intersection observer
                            const options = {
                                root: null,
                                rootMargin: '-30% 0% -30% 0%',
                                threshold: 0,
                            };

                            const callback = (entries:any[]) => {
                                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                                const interSectingEntry = entries?.find(x=>x.isIntersecting);
                                if(interSectingEntry)
                                {
                                    interSectingEntry.target.style.opacity='1';
                                    const allNonInterSectingEntry= entries.filter(x=>!x.isIntersecting);
                                    if (allNonInterSectingEntry) for (const element of allNonInterSectingEntry) {
                                        element.target.style.opacity='0';
                                    }
                                }
                                
                                
                            };

                            const cardPositionObserver = new IntersectionObserver(callback, options);

                            for (const card of cards) {
                                cardPositionObserver.observe(card);
                            }
                        }

                        

                    };

                    window.addEventListener('scroll', handleScroll);
                    listeners.push([window, 'scroll', handleScroll]);
                }

            };

            const observerInitialiseParallax = new IntersectionObserver(initialiseParallaxBackgroundLock, {
                threshold: 0.1,
            });

            for (const modal of modals) {
                // let scrollPostion = 0;

                const handleModalShow = () => {
                    const modalBackground = rootNode.querySelector<HTMLElement>('.modal-backdrop');
                    modalBackground?.classList.add('!top-auto');
                };

                modal.addEventListener('shown.bs.modal', handleModalShow);
                listeners.push([modal, 'shown.bs.modal', handleModalShow]);
            }

            initialiseParallaxScroll(parallaxBlock);

            window.addEventListener('resize', () => initialiseParallaxScroll(parallaxBlock));
            observerInitialiseParallax.observe(parallaxContainer);
        }
    }
}

export function unMountParallaxScrollBlock(
    listeners: Array<[Element | Window | Document, string, EventListenerOrEventListenerObject]>
) {
    for (const [element, event, listener] of listeners) {
        element.removeEventListener(event, listener);
    }
}

function between(x: number, min: number, max: number) {
    return x >= min && x <= max;
}
